import {
  ApiErrorSchema, ProfitabilitySchema, HealthResponseSchema, MeSchema, LocationSchema, RoleSchema, UserSchema, PaymentMethodSchema,
  MovementTypeSchema, ProductSchema, NextRefSchema, ProductDetailSchema, VariantSchema, CurveSchema, CatalogImportResultSchema,
  IdResultSchema, DocResultSchema, ShipmentSchema, SaleSchema, BodegaSalesSchema, CountSchema, StockRowSchema, StockByProductSchema, MovementSchema,
  ReceptionDifferenceSchema, KardexCheckSchema, StockSummarySchema, CashSummarySchema, CashHandoverSchema, CashHandoverResultSchema, CashBaseResultSchema, PurchaseRequestSchema, DecideResultSchema, SupplySchema, SupplySettingsSchema, SalesReportSchema, ProductsReportSchema, SummaryReportSchema, UserPermissionsResultSchema, CleanupBoardSchema, CleanupBulkResultSchema, MinimumBodySchema, CreditAccountSchema, CreditTransactionSchema, CreditPaymentResultSchema, DueDateBodySchema, ResolveSurplusResultSchema, InventoryImportResultSchema, DispatchImportRowResultSchema, DashboardSchema,
  type HealthResponse, type Me, type Language, type CreateProductBody, type CatalogImportRow, type EntryBody, type ShipmentBody,
  type ReceiveBody, type SaleBody, type ReturnBody, type CountBody, type InventoryImportBody, type DispatchImportRow,
} from '@teo/shared';
import { z } from 'zod';

/** Error devuelto por la API, con código estable para traducir en la interfaz. */
export class ApiRequestError extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly params: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

interface Options {
  method?: Method;
  body?: unknown;
  /** Clave de idempotencia (obligatoria en operaciones de negocio). */
  key?: string;
}

/** Página de una lista: las filas y el total de la consulta completa (cabecera X-Total-Count). */
export interface Page<T> {
  items: T[];
  total: number;
  /** Unidades de todo el filtro (cabecera X-Total-Units), cuando la ruta las informa. */
  units?: number;
  /** Saldo de todo el filtro (cabecera X-Total-Balance), en la cartera. */
  balance?: number;
}

async function request<T>(path: string, schema: z.ZodType<T>, opts: Options = {}): Promise<T> {
  return (await send(path, schema, opts)).data;
}

async function requestPage<T>(path: string, item: z.ZodType<T>): Promise<Page<T>> {
  const { data, res } = await send(path, z.array(item));
  const total = Number(res.headers.get('x-total-count'));
  const units = Number(res.headers.get('x-total-units'));
  const balance = Number(res.headers.get('x-total-balance'));
  return {
    items: data,
    total: Number.isFinite(total) && total >= data.length ? total : data.length,
    ...(res.headers.has('x-total-units') && Number.isFinite(units) ? { units } : {}),
    ...(res.headers.has('x-total-balance') && Number.isFinite(balance) ? { balance } : {}),
  };
}

async function send<T>(path: string, schema: z.ZodType<T>, opts: Options = {}): Promise<{ data: T; res: Response }> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (opts.body !== undefined) headers['content-type'] = 'application/json';
  if (opts.key) headers['idempotency-key'] = opts.key;
  let res: Response;
  try {
    res = await fetch(path, {
      method: opts.method ?? 'GET',
      credentials: 'same-origin',
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch {
    throw new ApiRequestError('NETWORK_ERROR', 0);
  }
  if (res.status === 204) return { data: schema.parse(null), res };
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const parsed = ApiErrorSchema.safeParse(body);
    if (parsed.success) throw new ApiRequestError(parsed.data.error.code, res.status, parsed.data.error.params);
    throw new ApiRequestError('INTERNAL_ERROR', res.status);
  }
  return { data: schema.parse(body), res };
}

/** Arma la cadena de consulta omitiendo los valores vacíos. */
function query(params: Record<string, string | number | boolean | undefined | null>): string {
  const qs = new URLSearchParams();
  // false sí viaja: hay filtros que significan "muéstrame todo" (only_alerts=false, only_available=false).
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  const s = qs.toString();
  return s ? `?${s}` : '';
}

const Nothing = z.null();
const IdList = z.array(z.number().int());

export const api = {
  health: (): Promise<HealthResponse> => request('/api/health', HealthResponseSchema),

  // ----- sesión -----
  login: (user: string, password: string): Promise<Me> => request('/api/auth/login', MeSchema, { method: 'POST', body: { user, password } }),
  logout: () => request('/api/auth/logout', Nothing, { method: 'POST' }),
  me: (): Promise<Me> => request('/api/me', MeSchema),
  setPreferences: (preferred_language: Language) =>
    request('/api/me/preferences', z.object({ preferred_language: z.enum(['es', 'en']) }), { method: 'PATCH', body: { preferred_language } }),
  changePassword: (current_password: string, new_password: string) =>
    request('/api/me/password', Nothing, { method: 'POST', body: { current_password, new_password } }),

  // ----- configuración -----
  locations: () => request('/api/locations', z.array(LocationSchema)),
  createLocation: (key: string, body: { code: string; name: string; type: 'WAREHOUSE' | 'STORE'; can_distribute: boolean; min_total: number }) =>
    request('/api/locations', IdResultSchema, { method: 'POST', body, key }),
  updateLocation: (key: string, id: number, body: { name: string; can_distribute: boolean; min_total: number; active: boolean }) =>
    request(`/api/locations/${id}`, IdResultSchema, { method: 'PATCH', body, key }),
  setFixedCosts: (key: string, id: number, body: { rent: number; payroll: number; other_fixed: number }) =>
    request(`/api/locations/${id}/fixed-costs`, IdResultSchema.extend({ fixed_total: z.number() }), { method: 'PUT', body, key }),
  profitability: (params: { from?: string; to?: string; all?: boolean } = {}) => request(`/api/locations/profitability${query(params)}`, ProfitabilitySchema),
  credits: (params: { status?: string; location_id?: number; q?: string; overdue?: boolean; limit?: number; offset?: number } = {}) =>
    requestPage(`/api/credits${query(params)}`, CreditAccountSchema),
  creditTransactions: (id: number) => request(`/api/credits/${id}/transactions`, z.array(CreditTransactionSchema)),
  payCredit: (key: string, id: number, body: { amount: number; payment_method_id: number; note?: string }) =>
    request(`/api/credits/${id}/payments`, CreditPaymentResultSchema, { method: 'POST', body, key }),
  setDueDate: (key: string, id: number, due_date: string | null) =>
    request(`/api/credits/${id}/due-date`, DueDateBodySchema, { method: 'PUT', body: { due_date }, key }),
  reportSales: (params: Record<string, unknown> = {}) => request(`/api/reports/sales${query(params as never)}`, SalesReportSchema),
  reportProducts: (params: Record<string, unknown> = {}) => request(`/api/reports/products${query(params as never)}`, ProductsReportSchema),
  reportSummary: (params: Record<string, unknown> = {}) => request(`/api/reports/summary${query(params as never)}`, SummaryReportSchema),
  cleanupBoard: (params: Record<string, unknown> = {}) => request(`/api/catalog/cleanup${query(params as never)}`, CleanupBoardSchema),
  cleanupBulk: (key: string, body: { product_ids: number[]; lifecycle: string; reason: string }) =>
    request('/api/catalog/cleanup', CleanupBulkResultSchema, { method: 'POST', body, key }),
  supply: (params: { location_id?: number; only_alerts?: boolean } = {}) => request(`/api/supply${query(params)}`, SupplySchema),
  saveSupplySettings: (key: string, body: { restock_factor: number; amber_pct: number; red_pct: number }) =>
    request('/api/supply/settings', SupplySettingsSchema, { method: 'PUT', body, key }),
  saveMinimum: (key: string, body: { location_id: number; product_id: number; min_stock: number | null }) =>
    request('/api/supply/minimums', MinimumBodySchema, { method: 'PUT', body, key }),
  purchaseRequests: (params: { status?: string; location_id?: number; limit?: number; offset?: number } = {}) =>
    requestPage(`/api/purchase-requests${query(params)}`, PurchaseRequestSchema),
  createPurchaseRequest: (key: string, body: { location_id: number; variant_id?: number; proposed?: Record<string, string>; qty: number; estimated_cost: number; note?: string }) =>
    request('/api/purchase-requests', DocResultSchema, { method: 'POST', body, key }),
  decidePurchase: (key: string, id: number, body: { approve: boolean; note?: string; product?: Record<string, unknown> }) =>
    request(`/api/purchase-requests/${id}/decision`, DecideResultSchema, { method: 'POST', body, key }),
  receivePurchase: (key: string, id: number, body: { qty: number; unit_cost: number; supplier?: string; note?: string }) =>
    request(`/api/purchase-requests/${id}/receive`, DocResultSchema, { method: 'POST', body, key }),
  cashSummary: (params: { location_id?: number; from?: string; to?: string } = {}) => request(`/api/cash/summary${query(params)}`, CashSummarySchema),
  cashHandovers: (params: { location_id?: number; from?: string; to?: string; limit?: number; offset?: number } = {}) =>
    requestPage(`/api/cash/handovers${query(params)}`, CashHandoverSchema),
  createHandover: (key: string, body: { location_id: number; amount: number; recipient: string; note?: string }) =>
    request('/api/cash/handovers', CashHandoverResultSchema, { method: 'POST', body, key }),
  setCashBase: (key: string, body: { location_id: number; amount: number; note?: string }) =>
    request('/api/cash/base', CashBaseResultSchema, { method: 'POST', body, key }),
  roles: () => request('/api/roles', z.array(RoleSchema)),
  users: () => request('/api/users', z.array(UserSchema)),
  createUser: (key: string, body: { username: string; email: string | null; full_name: string; password: string; role_code: string; location_ids: number[]; preferred_language: Language }) =>
    request('/api/users', IdResultSchema, { method: 'POST', body, key }),
  updateUser: (key: string, id: number, body: { email: string | null; full_name: string; role_code: string; location_ids: number[]; active: boolean }) =>
    request(`/api/users/${id}`, IdResultSchema, { method: 'PATCH', body, key }),
  resetPassword: (key: string, id: number, new_password: string) =>
    request(`/api/users/${id}/reset-password`, Nothing, { method: 'POST', body: { new_password }, key }),
  paymentMethods: () => request('/api/payment-methods', z.array(PaymentMethodSchema)),
  createPaymentMethod: (key: string, body: { code: string; name: string; kind: string; active: boolean; sort_order: number }) =>
    request('/api/payment-methods', IdResultSchema, { method: 'POST', body, key }),
  updatePaymentMethod: (key: string, id: number, body: { name: string; active: boolean; sort_order: number }) =>
    request(`/api/payment-methods/${id}`, IdResultSchema, { method: 'PATCH', body, key }),
  movementTypes: () => request('/api/movement-types', z.array(MovementTypeSchema)),

  // ----- catálogo -----
  products: (params: { q?: string; lifecycle?: string; include_archived?: boolean; below_cost?: boolean; limit?: number; offset?: number } = {}) =>
    requestPage(`/api/products${query(params)}`, ProductSchema),
  product: (id: number) => request(`/api/products/${id}`, ProductDetailSchema),
  nextRef: () => request('/api/products/next-ref', NextRefSchema),
  createProduct: (key: string, body: CreateProductBody) => request('/api/products', IdResultSchema, { method: 'POST', body, key }),
  updateProduct: (key: string, id: number, body: Partial<Omit<CreateProductBody, 'ref' | 'barcode' | 'stock_granularity' | 'variants'>>) =>
    request(`/api/products/${id}`, IdResultSchema, { method: 'PATCH', body, key }),
  setUserPermissions: (key: string, id: number, permissions: string[]) =>
    request(`/api/users/${id}/permissions`, UserPermissionsResultSchema, { method: 'PUT', body: { permissions }, key }),
  setLifecycle: (key: string, id: number, lifecycle: string, reason: string) =>
    request(`/api/products/${id}/lifecycle`, IdResultSchema.extend({ lifecycle: z.string() }), { method: 'POST', body: { lifecycle, reason }, key }),
  addVariants: (key: string, id: number, variants: { color?: string; size?: string }[]) =>
    request(`/api/products/${id}/variants`, z.object({ variant_ids: IdList }), { method: 'POST', body: { variants }, key }),
  changeControl: (key: string, id: number, stock_granularity: string, variants: { color?: string; size?: string }[]) =>
    request(`/api/products/${id}/control`, IdResultSchema.extend({ stock_granularity: z.string(), variants: z.number().int() }), { method: 'POST', body: { stock_granularity, variants }, key }),
  variants: (q: string) => request(`/api/variants?q=${encodeURIComponent(q)}`, z.array(VariantSchema)),
  curves: () => request('/api/curves', z.array(CurveSchema)),
  createCurve: (key: string, body: { code: string; name: string; active: boolean; lines: { variant_id: number; units: number }[] }) =>
    request('/api/curves', IdResultSchema, { method: 'POST', body, key }),
  updateCurve: (key: string, id: number, body: { code: string; name: string; active: boolean; lines: { variant_id: number; units: number }[] }) =>
    request(`/api/curves/${id}`, IdResultSchema, { method: 'PUT', body, key }),
  importCatalogPreview: (rows: CatalogImportRow[], source: string) =>
    request('/api/products/import/preview', CatalogImportResultSchema, { method: 'POST', body: { rows, source } }),
  importCatalogCommit: (key: string, rows: CatalogImportRow[], source: string) =>
    request('/api/products/import/commit', CatalogImportResultSchema, { method: 'POST', body: { rows, source }, key }),

  // ----- operación -----
  inventory: (params: { location_id?: number; q?: string; only_available?: boolean; variant_ids?: string; limit?: number } = {}) =>
    request(`/api/inventory${query(params)}`, z.array(StockRowSchema)),
  inventoryByProduct: (params: { location_id?: number; q?: string; limit?: number; offset?: number } = {}) =>
    requestPage(`/api/inventory/by-product${query(params)}`, StockByProductSchema),
  inventorySummary: (location_id?: number) => request(`/api/inventory/summary${query({ location_id })}`, StockSummarySchema),
  createEntry: (key: string, body: EntryBody, own = false) => request(own ? '/api/own-entries' : '/api/entries', DocResultSchema, { method: 'POST', body, key }),
  shipments: (params: { status?: string; destination_id?: number; origin_id?: number; limit?: number } = {}) =>
    request(`/api/shipments${query(params)}`, z.array(ShipmentSchema)),
  createShipment: (key: string, body: ShipmentBody) => request('/api/shipments', DocResultSchema, { method: 'POST', body, key }),
  receiveShipment: (key: string, id: number, body: ReceiveBody) => request(`/api/shipments/${id}/receive`, DocResultSchema, { method: 'POST', body, key }),
  receptionDifferences: (params: { status?: string; only_surplus?: boolean } = {}) =>
    request(`/api/reception-differences${query(params)}`, z.array(ReceptionDifferenceSchema)),
  resolveSurplus: (key: string, id: number, body: { accept: boolean; reason: string }) =>
    request(`/api/reception-differences/${id}/resolve`, ResolveSurplusResultSchema, { method: 'POST', body, key }),
  sales: (params: { location_id?: number; channel?: 'store' | 'direct'; from?: string; to?: string; limit?: number } = {}) =>
    request(`/api/sales${query(params)}`, z.array(SaleSchema)),
  warehouseSales: (params: { from?: string; to?: string } = {}) => request(`/api/warehouse/sales${query(params)}`, BodegaSalesSchema),
  createSale: (key: string, body: SaleBody) => request('/api/sales', DocResultSchema, { method: 'POST', body, key }),
  createReturn: (key: string, saleId: number, body: ReturnBody) => request(`/api/sales/${saleId}/returns`, DocResultSchema, { method: 'POST', body, key }),
  counts: (location_id?: number) => request(`/api/counts${query({ location_id })}`, z.array(CountSchema)),
  createCount: (key: string, body: CountBody) => request('/api/counts', DocResultSchema, { method: 'POST', body, key }),
  commitCount: (key: string, id: number, reason: string, confirm_full = false) =>
    request(`/api/counts/${id}/commit`, DocResultSchema, { method: 'POST', body: { reason, confirm_full }, key }),
  reverseDocument: (key: string, id: number, reason: string) => request(`/api/documents/${id}/reverse`, DocResultSchema, { method: 'POST', body: { reason }, key }),
  movements: (params: { location_id?: number; movement_type?: string; q?: string; from?: string; to?: string; limit?: number } = {}) =>
    request(`/api/movements${query(params)}`, z.array(MovementSchema)),
  /** Kardex paginado en el servidor (la pantalla Movimientos). */
  movementsPage: (params: { location_id?: number; movement_type?: string; q?: string; limit: number; offset: number }) =>
    requestPage(`/api/movements${query(params)}`, MovementSchema),
  kardexCheck: () => request('/api/kardex-check', KardexCheckSchema),

  // ----- importación de inventario, despacho masivo y dashboard -----
  importInventoryPreview: (body: InventoryImportBody) => request('/api/inventory/import/preview', InventoryImportResultSchema, { method: 'POST', body }),
  importInventoryCommit: (key: string, body: InventoryImportBody) => request('/api/inventory/import/commit', InventoryImportResultSchema, { method: 'POST', body, key }),
  importDispatchPreview: (origin_id: number, rows: DispatchImportRow[]) =>
    request('/api/shipments/import/preview', z.array(DispatchImportRowResultSchema), { method: 'POST', body: { origin_id, rows } }),
  dashboard: (params: { from?: string; to?: string; all?: boolean } = {}) => request(`/api/dashboard${query(params)}`, DashboardSchema),
};
